import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import Loading from '$/components/loading/Loading';
import * as Bixby from '$/core';
import { ReactComponent as Padlock } from '$/assets/icons/padlock.svg';
import './index.scss';

interface ITranscriptionItem {
  id: string;
  companyName: string;
  title: string;
  date: Date;
  aboutUrl: string;
  aboutTitle: string;
  status: Bixby.Enums.TranscriptionStatus;
  isPrivateMNPI?: boolean;
  hideTopBorder?: boolean;
  handleDownloadTranscriptAudio: (id: string, title: string) => void;
  handleDownloadTranscriptText: (id: string, title: string) => void;
  handleRetrieveTranscriptText: (id: string) => Promise<string>;
  handleRetrieveTranscriptTextFailure: () => void;
}

const TranscriptionItem: React.FC<ITranscriptionItem> = props => {
  const {
    id,
    title,
    date,
    aboutUrl,
    aboutTitle,
    isPrivateMNPI,
    hideTopBorder,
    handleDownloadTranscriptAudio,
    handleDownloadTranscriptText,
    handleRetrieveTranscriptText,
    handleRetrieveTranscriptTextFailure,
  } = props;

  const [isRetrievingTranscriptLoading, setIsRetrievingTranscriptLoading] = useState(false);
  const [isDowloadingTranscriptLoading, setIsDowloadingTranscriptLoading] = useState(false);
  const [isDowloadingAudioLoading, setIsDowloadingAudioLoading] = useState(false);

  const [transcript, setTranscript] = useState(undefined as undefined | string);

  const [showExpand, setShowExpand] = useState(true);

  const newLineRegex = /\r\n/g;

  const retrieveEntry = async () => {
    if (showExpand) {
      try {
        if (!transcript) {
          setIsRetrievingTranscriptLoading(true);
          const content: string = await handleRetrieveTranscriptText(id);
          const richText = content.replace(newLineRegex, '<br />');
          const el = document.createElement('html');
          el.innerHTML = richText;
          // MsoNormalTable is a class generated by CloudConvert.
          // If the CloudConvert implementation changes where this class is no longer present, consider depending on specific nodes or other classes
          const found = el.getElementsByClassName('MsoNormalTable');
          const text = found[found.length - 1]?.innerHTML;
          const parsed = (text || '').replace(/<br> /g, '');
          setTranscript(parsed);
        }
        setShowExpand(false);
      } catch (e: any) {
        handleRetrieveTranscriptTextFailure();
      } finally {
        setIsRetrievingTranscriptLoading(false);
      }
    } else {
      setShowExpand(true);
    }
  };

  const downloadTranscript = async () => {
    setIsDowloadingTranscriptLoading(true);
    try {
      await handleDownloadTranscriptText(id, title);
    } finally {
      setIsDowloadingTranscriptLoading(false);
    }
  };

  const downloadAudio = async () => {
    setIsDowloadingAudioLoading(true);
    try {
      await handleDownloadTranscriptAudio(id, title);
    } finally {
      setIsDowloadingAudioLoading(false);
    }
  };

  const formatDate = (d: any) => {
    const transcriptionDate = Bixby.Utils.utcDateToString(new Date(d));
    return Bixby.Utils.formatDate(transcriptionDate);
  };

  return (
    <div className={classnames('transcription_item', { hide_top_border: hideTopBorder })}>
      <div className="title_contain">
        <div className="item_title">{title}</div>
        {isPrivateMNPI && (
          <div className="padlock" role="presentation" title="Private Declaration">
            <Padlock />
          </div>
        )}
      </div>
      <div className="item_info">
        <div>
          {date && formatDate(date)} | About:{' '}
          <Link to={aboutUrl} className="about_link">
            {aboutTitle}
          </Link>
        </div>
      </div>
      <div className="hide_print">
        <div onClick={downloadTranscript} className={'download'} role="button">
          <span>Download Transcript</span>
        </div>
        {isDowloadingTranscriptLoading && <Loading inline />}
        <div onClick={downloadAudio} className="download" role="button">
          <span>Download Audio</span>
        </div>
        {isDowloadingAudioLoading && <Loading inline />}
      </div>
      {Bixby.Utils.isDefined(transcript) && (
        <div className={classnames('transcription_item_content', { active: !showExpand })}>
          <div
            className={classnames('show_print', { expanded: !showExpand })}
            dangerouslySetInnerHTML={{ __html: transcript ?? '' }}></div>
        </div>
      )}
      <div onClick={retrieveEntry} className="expand hide_print" role="button">
        {showExpand && <span>View Transcript</span>}
        {!showExpand && <span>Hide Transcript</span>}
      </div>
      {isRetrievingTranscriptLoading && <Loading inline />}
    </div>
  );
};

export default TranscriptionItem;
